/**
 * Pure URL + cache-key + fallback-chain logic for the SpriteResolver. No Phaser,
 * no network — just the path scheme the one-time ingest wrote, mirrored to the
 * runtime. Kept pure so the fallback SELECTION is unit-testable and deterministic.
 *
 * On-disk layout produced by the ingest (see data/ingest/lib/{sprites,pmd}.ts):
 *   /generated/sprites/<dexNo>/front-anim.gif | back-anim.gif   (tier 1: animated BW)
 *   /generated/sprites/<dexNo>/front.png      | back.png        (tier 2: static BW)
 *   /generated/sprites/<dexNo>/official.png                     (tier 3: official art)
 *   /generated/sprites/<dexNo>/placeholder.png                  (tier 4: 1x1 neutral)
 *   /generated/pmd/<dexNo>/overworld/Walk-Anim.png + AnimData.xml (8-dir walk sheet)
 *   /generated/pmd/<dexNo>/overworld/Idle-Anim.png
 *   /generated/pmd/<dexNo>/portraits/<Emotion>.png              (emotion portraits)
 *   /generated/pmd/<dexNo>/portrait-sheet.png                   (combined sheet)
 */
import type { Emotion } from '@/shared';

/** Root the runtime serves generated assets from (Vite static dir / nginx). */
export const GENERATED_ROOT = '/generated';

/** Root the Project Pokemon 3D-model RENDER spritesheets are served from. */
export const RENDERS_ROOT = '/generated/renders';

/**
 * Runtime URL of a species' 3D-model render spritesheet for a facing — a VERTICAL
 * PNG (frames stacked top->bottom). This mirrors the manifest's `path`, but the
 * manifest is the source of truth for the per-frame dimensions; this is only used as
 * a defensive fallback when a side's `path` is somehow absent. e.g.
 * `/generated/renders/1/front.png`.
 */
export function renderUrl(dexNo: number, facing: Facing): string {
  return `${RENDERS_ROOT}/${dexNo}/${facing}.png`;
}

/** Which battle-sprite tier a URL represents (drives the fallback order). */
export type BattleTier = 'animated' | 'static' | 'official' | 'placeholder';

export type Facing = 'front' | 'back';

/** 8-direction overworld facing (PMD walk sheets are laid out by these). */
export type Direction =
  | 'south'
  | 'south-east'
  | 'east'
  | 'north-east'
  | 'north'
  | 'north-west'
  | 'west'
  | 'south-west';

/**
 * PMD walk/idle sheets order rows by direction starting at South, going
 * counter-clockwise. This index is the row in the sheet for a given facing.
 */
export const PMD_DIRECTION_ORDER: readonly Direction[] = [
  'south',
  'south-east',
  'east',
  'north-east',
  'north',
  'north-west',
  'west',
  'south-west',
];

export function directionRow(dir: Direction): number {
  const i = PMD_DIRECTION_ORDER.indexOf(dir);
  return i < 0 ? 0 : i;
}

/**
 * Ordered candidate URLs for a species' BATTLE sprite of a given facing, best
 * tier first. The resolver tries each in order and stops at the first that loads;
 * the final placeholder always exists (ingest writes a 1x1 PNG), so resolution
 * never dead-ends.
 */
export function battleSpriteCandidates(dexNo: number, facing: Facing): readonly string[] {
  const base = `${GENERATED_ROOT}/sprites/${dexNo}`;
  if (facing === 'back') {
    return [
      `${base}/back-anim.gif`,
      `${base}/back.png`,
      // official art / placeholder have no back variant — reuse the front-side fallbacks.
      `${base}/official.png`,
      `${base}/placeholder.png`,
    ];
  }
  return [
    `${base}/front-anim.gif`,
    `${base}/front.png`,
    `${base}/official.png`,
    `${base}/placeholder.png`,
  ];
}

/** The tier a battle candidate URL belongs to (for coverage/telemetry). */
export function tierOf(url: string): BattleTier {
  if (url.endsWith('-anim.gif')) return 'animated';
  if (url.endsWith('placeholder.png')) return 'placeholder';
  if (url.endsWith('official.png')) return 'official';
  return 'static';
}

/** The combined portrait sheet URL (all emotions in one image, if ingested). */
export function portraitSheetUrl(dexNo: number): string {
  return `${GENERATED_ROOT}/pmd/${dexNo}/portrait-sheet.png`;
}

/**
 * Map the frozen lowercase `Emotion` tag to the PMD portrait file name. PMD ships
 * a fixed set (Normal/Happy/Sad/Angry/Surprised/Pain/Sigh + more); emotions we
 * request that PMD may not have are aliased to the nearest available expression so
 * the chat layer always shows *a* matching face rather than nothing.
 */
const EMOTION_TO_PMD: Record<Emotion, string> = {
  normal: 'Normal',
  happy: 'Happy',
  sad: 'Sad',
  angry: 'Angry',
  surprised: 'Surprised',
  worried: 'Sad',
  joyous: 'Happy',
  determined: 'Normal',
  sigh: 'Sigh',
  inspired: 'Surprised',
};

export function pmdEmotionName(emotion: Emotion): string {
  return EMOTION_TO_PMD[emotion] ?? 'Normal';
}

/**
 * Ordered candidate URLs for a species' emotion PORTRAIT, best first. Falls back
 * to the neutral Normal portrait, then the species' battle sprite, then the
 * placeholder — so a chat line always pairs with a face.
 */
export function portraitCandidates(dexNo: number, emotion: Emotion): readonly string[] {
  const base = `${GENERATED_ROOT}/pmd/${dexNo}/portraits`;
  const name = pmdEmotionName(emotion);
  const out: string[] = [`${base}/${name}.png`];
  if (name !== 'Normal') out.push(`${base}/Normal.png`);
  // Last resort: the battle front sprite (covers PMD-less species), then placeholder.
  out.push(`${GENERATED_ROOT}/sprites/${dexNo}/front.png`);
  out.push(`${GENERATED_ROOT}/sprites/${dexNo}/placeholder.png`);
  return out;
}

/**
 * The runtime URL for a Pokemon Showdown TRAINER sprite (single front-facing PNG)
 * by its normalized key (e.g. 'red', 'lass', 'acetrainerf'). The ingest vendors these
 * to /generated/trainers/<key>.png and mirrors them to public/.
 */
export function trainerSpriteUrl(key: string): string {
  return `${GENERATED_ROOT}/trainers/${key}.png`;
}

/** The trainers manifest (the available keys), written by the ingest. */
export function trainersManifestUrl(): string {
  return `${GENERATED_ROOT}/trainers/trainers.json`;
}

/** The PMD walk anim sheet (8-direction) + its idle sheet, with AnimData metadata. */
export function overworldWalkUrl(dexNo: number): string {
  return `${GENERATED_ROOT}/pmd/${dexNo}/overworld/Walk-Anim.png`;
}
export function overworldIdleUrl(dexNo: number): string {
  return `${GENERATED_ROOT}/pmd/${dexNo}/overworld/Idle-Anim.png`;
}
export function animDataUrl(dexNo: number): string {
  return `${GENERATED_ROOT}/pmd/${dexNo}/AnimData.xml`;
}

/**
 * Stable Phaser texture cache KEY for a resolved asset. Keys are content-shaped
 * (kind + dexNo + variant) so identical requests share one texture — the basis of
 * sprite-pooling: a busy town with 50 Pidgey reuses a single decoded texture.
 */
export function textureKey(
  kind: 'battle' | 'portrait' | 'walk' | 'idle' | 'walksheet' | 'idlesheet' | 'render',
  dexNo: number,
  variant?: string,
): string {
  return variant ? `pr:${kind}:${dexNo}:${variant}` : `pr:${kind}:${dexNo}`;
}

/**
 * Stable global animation key for a render's gentle looping idle, per facing —
 * `<renderTextureKey>:idle`. Registered idempotently on first play; reused thereafter
 * so a busy dex / repeated battles share one animation per species side.
 */
export function renderAnimKey(renderTextureKey: string): string {
  return `${renderTextureKey}:idle`;
}
